import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { postJournal } from '../../api/journal';
import { useAuth } from '../../context/AuthContext';

function AccountSearch({ accounts, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef();

  const selected = accounts.find(a => a.id === value);
  const display = selected ? `${selected.account_code}  ${selected.account_name}` : '';

  const filtered = query.length > 0
    ? accounts.filter(a =>
        `${a.account_code} ${a.account_name}`.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 20)
    : accounts.slice(0, 20);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (id) => { onChange(id); setQuery(''); setOpen(false); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        className="form-input"
        style={{ margin: 0 }}
        placeholder="Search account..."
        value={open ? query : display}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
          background: 'var(--card-bg)', border: '1px solid var(--border)',
          borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          maxHeight: 240, overflowY: 'auto',
        }}>
          {filtered.length === 0
            ? <div style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 13 }}>No accounts found</div>
            : filtered.map(a => (
              <div key={a.id}
                onMouseDown={() => select(a.id)}
                style={{
                  padding: '8px 14px', cursor: 'pointer', fontSize: 13,
                  background: a.id === value ? 'var(--primary-light, rgba(59,130,246,0.08))' : 'transparent',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg, rgba(0,0,0,0.04))'}
                onMouseLeave={e => e.currentTarget.style.background = a.id === value ? 'var(--primary-light, rgba(59,130,246,0.08))' : 'transparent'}
              >
                <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)', marginRight: 8 }}>{a.account_code}</span>
                {a.account_name}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

export default function JournalForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';

  const [accounts, setAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [currency_id, setCurrencyId] = useState('');
  const [exchange_rate, setExchangeRate] = useState('1');
  const [lines, setLines] = useState([
    { account_id: '', debit_amount: '', credit_amount: '', description: '' },
    { account_id: '', debit_amount: '', credit_amount: '', description: '' },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    supabase.from('chart_of_accounts').select('id, account_code, account_name, account_types(category)').eq('is_active', true).order('account_code')
      .then(({ data }) => setAccounts(data || []));
    supabase.from('currencies').select('id, code').eq('is_active', true).order('code')
      .then(({ data }) => { setCurrencies(data || []); const lkr = data?.find(c => c.code === 'LKR'); setCurrencyId(lkr?.id || data?.[0]?.id || ''); });
  }, []);

  const setLine = (i, k, v) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines(ls => [...ls, { account_id: '', debit_amount: '', credit_amount: '', description: '' }]);
  const removeLine = (i) => setLines(ls => ls.filter((_, idx) => idx !== i));

  const totalDR = lines.reduce((s, l) => s + parseFloat(l.debit_amount || 0), 0);
  const totalCR = lines.reduce((s, l) => s + parseFloat(l.credit_amount || 0), 0);
  const isBalanced = Math.abs(totalDR - totalCR) < 0.01;

  const handleSubmit = async () => {
    if (!description) return setError('Description is required');
    if (!currency_id) return setError('Currency is required');
    const validLines = lines.filter(l => l.account_id && (parseFloat(l.debit_amount || 0) > 0 || parseFloat(l.credit_amount || 0) > 0));
    if (validLines.length < 2) return setError('At least 2 lines required');
    if (!isBalanced) return setError('Debits and credits must balance');
    setSaving(true); setError('');
    try {
      const je = await postJournal({
        date,
        description,
        currency_id,
        exchange_rate: parseFloat(exchange_rate) || 1,
        created_by: username,
        is_auto_posted: false,
        lines: validLines.map(l => ({
          account_id: l.account_id,
          debit_amount: parseFloat(l.debit_amount || 0),
          credit_amount: parseFloat(l.credit_amount || 0),
          description: l.description,
        })),
      });
      navigate(`/journals/${je.id}`);
    } catch (err) {
      setError(err.message || 'Error posting journal');
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Manual Journal Entry</div><div className="page-subtitle">Post adjustments and corrections</div></div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || !isBalanced}>{saving ? 'Posting...' : 'Post Journal'}</button>
        </div>
      </div>
      {error && <div style={{ color: 'var(--danger)', marginBottom: 16, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>{error}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Date *</label>
            <input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Currency *</label>
            <select className="form-input" value={currency_id} onChange={e => setCurrencyId(e.target.value)}>
              {currencies.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Exchange Rate (to LKR)</label>
            <input className="form-input" type="number" step="0.0001" value={exchange_rate} onChange={e => setExchangeRate(e.target.value)} />
          </div>
          <div className="form-group full-width">
            <label className="form-label">Description / Narration *</label>
            <input className="form-input" value={description} onChange={e => setDescription(e.target.value)} placeholder="What is this journal entry for?" />
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div className="card-title">Journal Lines</div>
          <button className="btn btn-secondary btn-sm" onClick={addLine}>+ Add Line</button>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th style={{ width: '35%' }}>Account</th>
                <th>Line Description</th>
                <th style={{ textAlign: 'right', width: 130 }}>Debit</th>
                <th style={{ textAlign: 'right', width: 130 }}>Credit</th>
                <th style={{ width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    <AccountSearch accounts={accounts} value={l.account_id} onChange={v => setLine(i, 'account_id', v)} />
                  </td>
                  <td>
                    <input className="form-input" style={{ margin: 0 }} value={l.description} onChange={e => setLine(i, 'description', e.target.value)} placeholder="Description" />
                  </td>
                  <td>
                    <input className="form-input" style={{ margin: 0, textAlign: 'right' }} type="number" step="0.01" value={l.debit_amount} onChange={e => { setLine(i, 'debit_amount', e.target.value); if (e.target.value) setLine(i, 'credit_amount', ''); }} placeholder="0.00" />
                  </td>
                  <td>
                    <input className="form-input" style={{ margin: 0, textAlign: 'right' }} type="number" step="0.01" value={l.credit_amount} onChange={e => { setLine(i, 'credit_amount', e.target.value); if (e.target.value) setLine(i, 'debit_amount', ''); }} placeholder="0.00" />
                  </td>
                  <td>
                    {lines.length > 2 && <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 16, padding: '0 4px' }} onClick={() => removeLine(i)}>x</button>}
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                <td colSpan={2} style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: 12 }}>TOTALS</td>
                <td style={{ textAlign: 'right', color: 'var(--danger)' }} className="amount">{totalDR.toLocaleString()}</td>
                <td style={{ textAlign: 'right', color: 'var(--success)' }} className="amount">{totalCR.toLocaleString()}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 12, textAlign: 'right', fontSize: 13 }}>
          {isBalanced && totalDR > 0
            ? <span style={{ color: 'var(--success)', fontWeight: 600 }}>Balanced</span>
            : <span style={{ color: 'var(--danger)' }}>Difference: {Math.abs(totalDR - totalCR).toLocaleString()}</span>
          }
        </div>
      </div>
    </div>
  );
}
